import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import type { CoreRange } from "@CommonUtils/cdtStudio/domain/aestheticsScore";
import type { Pog } from "@CommonTypes/merchflow/pog/pog";
import type { ResponseGetMerchflowCdtRelease } from "@CommonApi/merchflows/_merchflowId_/cdt/release";
import {
  releaseAestheticsScore,
  spineOfRelease,
} from "@CommonUtils/cdtStudio/domain/pogAesthetics";
import { fetchCdtRelease } from "src/pages/CdtStudio/store/cdtCompressionService";
import { categoryLengthUnitOf } from "src/pages/CdtStudio/domain/studioLengthUnit";
import {
  fetchStorePogSentRequest,
  type StorePogSentRequest,
} from "src/pages/CdtStudio/store/storePogSentRestrictions";

type Release = ResponseGetMerchflowCdtRelease | null;

/** What a store pog is graded against: its CDT release, and what its generation was sent. */
export interface StorePogGrading {
  /** UI-only explicit core configuration; native sent.coreRange grading remains unchanged. */
  reviewCoreRange?: CoreRange;
  release: Release;
  /** The release lookup failed (a 404 is no release, not a failure): the plan is not graded. */
  releaseFailed: boolean;
  sent: StorePogSentRequest | null;
  /** The sent-request lookup failed (none sent is `sent: null`): the plan is not graded. */
  sentFailed: boolean;
}

/** A part of what the plan is graded against is unknown, so no score is given or written. */
export const gradingFailed = (grading: StorePogGrading | null | undefined) =>
  !!grading && (grading.releaseFailed || grading.sentFailed);

/* An answer is kept long enough for the review player's scoring pass and the plan it then shows
   to share one request, and no longer: a new release reaches the next page that asks. */
const GRADING_TTL_MS = 60_000;
/* A release is a whole CDT tree and a merchflow's plans share a few, so a handful are kept; a sent
   request is one plan's, kept for the plans around the one on screen. */
export const RELEASES_KEPT = 8;
export const SENT_REQUESTS_KEPT = 16;

interface Cached<T> {
  at: number;
  request: Promise<T>;
}

const releases = new Map<string, Cached<{ release: Release; failed: boolean }>>();
const sentRequests = new Map<
  number,
  Cached<{ sent: StorePogSentRequest | null; failed: boolean }>
>();

/** Insertion order is age: a read moves the entry to the back, the front goes first. */
const remember = <K, T>(cache: Map<K, Cached<T>>, key: K, entry: Cached<T>, kept: number) => {
  cache.delete(key);
  cache.set(key, entry);
  for (const oldest of cache.keys()) {
    if (cache.size <= kept) break;
    cache.delete(oldest);
  }
};

const fresh = <K, T>(cache: Map<K, Cached<T>>, key: K) => {
  const cached = cache.get(key);
  if (!cached || Date.now() - cached.at >= GRADING_TTL_MS) return null;
  cache.delete(key);
  cache.set(key, cached);
  return cached.request;
};

/* Plans that name the same release share one fetch of it; a plan that names none is looked up on
   its own, as its detail page looks it up. */
const releaseOf = (merchflowId: number, storePogId: number, cdtReleaseId: number | null) => {
  const key =
    cdtReleaseId === null ? `pog:${merchflowId}/${storePogId}` : `release:${cdtReleaseId}`;
  const cached = fresh(releases, key);
  if (cached) return cached;
  const request = fetchCdtRelease(merchflowId, storePogId).then(
    (release) => {
      // A named release is evidence the plan was generated against that exact
      // contract; missing or different data must not become a legacy fallback.
      if (cdtReleaseId !== null && (!release || release.id !== cdtReleaseId)) {
        if (releases.get(key)?.request === request) releases.delete(key);
        return { release: null, failed: true };
      }
      return { release, failed: false };
    },
    () => {
      if (releases.get(key)?.request === request) releases.delete(key);
      return { release: null, failed: true };
    },
  );
  remember(releases, key, { at: Date.now(), request }, RELEASES_KEPT);
  return request;
};

const sentRequestOf = (storePogId: number) => {
  const cached = fresh(sentRequests, storePogId);
  if (cached) return cached;
  const request = fetchStorePogSentRequest(storePogId).then(
    (sent) => ({ sent, failed: false }),
    () => {
      if (sentRequests.get(storePogId)?.request === request) sentRequests.delete(storePogId);
      return { sent: null, failed: true };
    },
  );
  remember(sentRequests, storePogId, { at: Date.now(), request }, SENT_REQUESTS_KEPT);
  return request;
};

/**
 * What the store pog was generated against. A part it has none of (a 404) is null, and the plan is
 * graded on what it stores instead. A part whose lookup failed is `releaseFailed` or `sentFailed`
 * and is not kept, so the next caller asks again. `cdtReleaseId` is the release the review queue
 * names for the pog.
 */
export const loadStorePogGrading = async (
  merchflowId: number,
  storePogId: number,
  cdtReleaseId?: number | null,
): Promise<StorePogGrading> => {
  const [{ release, failed }, { sent, failed: sentFailed }] = await Promise.all([
    releaseOf(merchflowId, storePogId, cdtReleaseId ?? null),
    sentRequestOf(storePogId),
  ]);
  return {
    release,
    releaseFailed: failed,
    sent,
    sentFailed,
    ...(sent?.reviewCoreRange ? { reviewCoreRange: sent.reviewCoreRange } : {}),
  };
};

/** Tests only: forget every grading asked for so far. */
export const forgetStorePogGradings = () => {
  releases.clear();
  sentRequests.clear();
};

/** Tests only: how many releases and sent requests are held. */
export const heldStorePogGradings = () => ({
  releases: releases.size,
  sentRequests: sentRequests.size,
});

/** The release's contract as the score reads it, or null when there is none to grade against. */
export const releaseSpineOf = (release: Release | undefined) =>
  release ? spineOfRelease(release.spine) : null;

/**
 * One grading wherever the plan is shown or stored: on its release's contract and what it was sent.
 * None when the release or the sent request failed to load: the plan's own columns are not its
 * contract, and a missing request would grade it without its restrictions, core range and blocking.
 */
export const gradeAesthetics = (pog: Pog | null, grading: StorePogGrading | null | undefined) =>
  gradingFailed(grading)
    ? null
    : releaseAestheticsScore(pog, {
        spine: grading?.release?.spine ?? null,
        releaseId: grading?.release?.id ?? null,
        restrictions: grading?.sent?.restrictions ?? null,
        coreRange: grading?.sent?.coreRange ?? null,
        blocking: grading?.sent?.blocking ?? null,
        lengthUnit: categoryLengthUnitOf(grading?.release),
      });

/**
 * The grading of the store pog given by id. With no id (a base pog) there is none to fetch.
 * `isLoading` holds a score until the answer is in, so it never shows one grading and then
 * another.
 */
export const useStorePogGradingById = (
  merchflowId: number | null,
  storePogId: number | null,
  cdtReleaseId?: number | null,
) => {
  const [retryKey, setRetryKey] = useState(0);
  const key =
    merchflowId && storePogId ? `${merchflowId}/${storePogId}/${cdtReleaseId}/${retryKey}` : null;
  const [answer, setAnswer] = useState<{ key: string; grading: StorePogGrading } | null>(null);

  useEffect(() => {
    if (!key || !merchflowId || !storePogId) return;
    let cancelled = false;
    void loadStorePogGrading(merchflowId, storePogId, cdtReleaseId).then((grading) => {
      if (!cancelled) setAnswer({ key, grading });
    });
    return () => {
      cancelled = true;
    };
  }, [cdtReleaseId, key, merchflowId, storePogId]);

  const settled = !key || answer?.key === key;
  return {
    grading: key && answer?.key === key ? answer.grading : null,
    isLoading: !settled,
    retry: () => setRetryKey((value) => value + 1),
  };
};

const positiveId = (value: string | undefined) => {
  const id = Number(value);
  return Number.isInteger(id) && id > 0 ? id : null;
};

/**
 * On a store pog's page, what that pog was generated against. Off one (a base pog, a page with
 * no store pog in its route) there is nothing to fetch and the plan is graded on what it stores.
 */
export const useStorePogGrading = () => {
  const params = useParams<{ merchflowId?: string; storePogId?: string }>();
  return useStorePogGradingById(positiveId(params.merchflowId), positiveId(params.storePogId));
};
